import { useState } from 'react'
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ReportPager, ReportSection } from '@/components/reports/ReportSection'
import { ReportStates, downloadCsv, tooltipStyle } from './reportShared'
import { useProcedureOnTimeReport } from '@/hooks/useProcedureVolumeReport'
import { reportFileName, toCsv } from '@/lib/reportCsv'
import type { ReportFilterSelection } from '@/lib/reportFilters'
import { windowLabel, type ReportTimeFrame } from '@/lib/reportRange'
import { PROCEDURE_TYPE_LABELS } from '@/types/cgt'
import type { OnTimeVerdict } from '@/api/reportsApi'

/**
 * Report 1.3 — on-time procedure completion.
 *
 * ## The rule, and the thing it must never do
 *
 * On time is completion at or before the due instant. A procedure with NO due
 * date is not on time and not late — it had no target to miss. Those are
 * counted apart and excluded from the rate, exactly as the ticket requires, so
 * the figure can never be silently inflated by work nobody set a target for.
 * The denominator is spelled out under the number rather than left implied.
 *
 * ## Why these charts
 *
 * A single rate is a stat, not a chart — so the headline IS the report, with a
 * split bar beneath showing what the window was made of, at the same width so
 * the parts read against the whole.
 *
 * The per-type chart is horizontal bars, not a pie: comparing four rates is a
 * length comparison, and horizontal bars leave room for real type names
 * instead of a legend the reader has to look back and forth against. Each bar
 * is direct-labelled with its own percentage, so the axis is a convenience
 * rather than the only way to read a value.
 */
const VERDICT: Record<OnTimeVerdict, { label: string; color: string }> = {
  on_time: { label: 'On time', color: '#1baf7a' },
  late: { label: 'Late', color: '#eb6834' },
  no_target: { label: 'No target set', color: '#9ca3af' },
}

/** Rate bands, so a bar's colour says how the number should feel. */
function rateColor(pct: number): string {
  if (pct >= 90) return '#1baf7a'
  if (pct >= 75) return '#eda100'
  return '#eb6834'
}

const PAGE_SIZE = 20

export function ProcedureOnTimeReport({
  frame,
  filters,
}: {
  frame: ReportTimeFrame
  filters: ReportFilterSelection
}) {
  const query = useProcedureOnTimeReport(frame, filters)
  const [page, setPage] = useState(1)
  const state = ReportStates(query)
  if (state) return state
  const report = query.data
  if (!report) return null

  const judged = report.onTime + report.late
  const totalCompleted = judged + report.noTarget

  const parts = (['on_time', 'late', 'no_target'] as OnTimeVerdict[])
    .map((v) => ({
      verdict: v,
      label: VERDICT[v].label,
      value: v === 'on_time' ? report.onTime : v === 'late' ? report.late : report.noTarget,
    }))
    .filter((p) => p.value > 0)

  const byType = report.byType
    .filter((t) => t.onTimePct !== null)
    .map((t) => ({
      name: PROCEDURE_TYPE_LABELS[t.type] ?? t.type,
      pct: t.onTimePct as number,
      onTime: t.onTime,
      late: t.late,
    }))

  const pageCount = Math.max(1, Math.ceil(report.rows.length / PAGE_SIZE))
  const current = Math.min(page, pageCount)
  const start = (current - 1) * PAGE_SIZE
  const rows = report.rows.slice(start, start + PAGE_SIZE)

  const exportCsv = () => {
    downloadCsv(
      toCsv(
        [
          'Completed',
          'Procedure type',
          'Template',
          'Work order',
          'Patient MRN',
          'Due',
          'Verdict',
          'Variance (days)',
        ],
        report.rows.map((r) => [
          r.completedAt,
          r.type ? (PROCEDURE_TYPE_LABELS[r.type] ?? r.type) : '',
          r.templateName,
          r.workOrderNumber,
          r.patientMrn,
          r.dueDate,
          VERDICT[r.verdict].label,
          r.varianceDays,
        ]),
      ),
      reportFileName('procedures-on-time', report.window),
    )
  }

  return (
    <div className='flex flex-col gap-4 p-4'>
      {/* ── The headline, full width, with the split directly beneath it ──── */}
      <ReportSection
        title='On-time completion'
        subtitle={windowLabel({ from: report.window.from, to: report.window.to })}
      >
        <div className='flex flex-col gap-4 p-4'>
          <div className='flex flex-wrap items-end gap-x-8 gap-y-3'>
            <div>
              <p
                className='text-5xl font-semibold tabular-nums'
                style={{ color: report.onTimePct === null ? undefined : rateColor(report.onTimePct) }}
              >
                {report.onTimePct === null ? '—' : `${report.onTimePct}%`}
              </p>
              <p className='mt-1 text-sm text-muted-foreground'>
                {report.onTimePct === null
                  ? 'Nothing completed with a due date'
                  : `${report.onTime} of ${judged} completed on or before their due date`}
              </p>
            </div>
            <dl className='flex flex-wrap gap-x-6 gap-y-2'>
              <Stat label='Completed' value={totalCompleted} />
              <Stat label='On time' value={report.onTime} color={VERDICT.on_time.color} />
              <Stat label='Late' value={report.late} color={VERDICT.late.color} />
              <Stat label='No target set' value={report.noTarget} color={VERDICT.no_target.color} />
            </dl>
          </div>

          {/* One bar, the width of the report: the parts of the window, to
              scale. Segments under 8% of the width carry no inline label,
              because a number that does not fit is worse than the legend. */}
          {totalCompleted > 0 && (
            <div>
              <div className='flex h-7 w-full overflow-hidden rounded-lg'>
                {parts.map((p, i) => (
                  <div
                    key={p.verdict}
                    title={`${p.label}: ${p.value}`}
                    className='flex items-center justify-center text-xs font-medium text-white'
                    style={{
                      width: `${(p.value / totalCompleted) * 100}%`,
                      background: VERDICT[p.verdict].color,
                      marginLeft: i === 0 ? 0 : 2,
                    }}
                  >
                    {p.value / totalCompleted > 0.08 ? p.value : ''}
                  </div>
                ))}
              </div>
              <div className='mt-2 flex flex-wrap gap-x-4 gap-y-1'>
                {parts.map((p) => (
                  <span
                    key={p.verdict}
                    className='flex items-center gap-1.5 text-xs text-muted-foreground'
                  >
                    <span
                      aria-hidden
                      className='h-2.5 w-2.5 rounded-sm'
                      style={{ background: VERDICT[p.verdict].color }}
                    />
                    {p.label} — {p.value} (
                    {Math.round((p.value / totalCompleted) * 1000) / 10}%)
                  </span>
                ))}
              </div>
            </div>
          )}

          {report.completedUndated > 0 && (
            <p className='text-xs text-muted-foreground'>
              {report.completedUndated} with no completion time, not judged.
            </p>
          )}
        </div>
      </ReportSection>

      {/* ── Rate per type ─────────────────────────────────────────────────── */}
      <ReportSection title='On-time rate by procedure type'>
        {byType.length === 0 ? (
          <p className='px-4 py-6 text-center text-xs text-muted-foreground'>
            Nothing to judge in this window.
          </p>
        ) : (
          <div className='w-full px-2 py-4' style={{ height: Math.max(140, byType.length * 46) }}>
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart
                data={byType}
                layout='vertical'
                margin={{ top: 4, right: 56, bottom: 4, left: 8 }}
              >
                <XAxis type='number' domain={[0, 100]} hide />
                <YAxis
                  type='category'
                  dataKey='name'
                  width={110}
                  tick={{ fontSize: 12, fill: '#374151' }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  contentStyle={tooltipStyle}
                  formatter={(value) => [`${value as number}%`, 'On time']}
                />
                <Bar dataKey='pct' radius={[0, 4, 4, 0]} barSize={18}>
                  {byType.map((t) => (
                    <Cell key={t.name} fill={rateColor(t.pct)} />
                  ))}
                  {/* Direct-labelled, so the bar is readable without an axis. */}
                  <LabelList
                    dataKey='pct'
                    position='right'
                    formatter={(v) => (v == null ? '' : `${v}%`)}
                    style={{ fontSize: 12, fill: '#374151' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </ReportSection>

      {/* ── The same figures as a table ───────────────────────────────────── */}
      <ReportSection title='Summary — by procedure type'>
        <div className='overflow-x-persistent'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground'>
                <th className='px-4 py-2 font-medium'>Procedure type</th>
                <th className='px-4 py-2 text-right font-medium'>On time</th>
                <th className='px-4 py-2 text-right font-medium'>Late</th>
                <th className='px-4 py-2 text-right font-medium'>No target</th>
                <th className='px-4 py-2 text-right font-medium'>Judged</th>
                <th className='px-4 py-2 text-right font-medium'>On-time rate</th>
              </tr>
            </thead>
            <tbody>
              {report.byType.length === 0 && (
                <tr>
                  <td colSpan={6} className='px-4 py-6 text-center text-xs text-muted-foreground'>
                    Nothing completed in this window.
                  </td>
                </tr>
              )}
              {report.byType.map((t) => (
                <tr key={t.type} className='border-b last:border-0'>
                  <td className='px-4 py-2'>{PROCEDURE_TYPE_LABELS[t.type] ?? t.type}</td>
                  <td className='px-4 py-2 text-right tabular-nums'>{t.onTime}</td>
                  <td className='px-4 py-2 text-right tabular-nums'>{t.late}</td>
                  <td className='px-4 py-2 text-right tabular-nums text-muted-foreground'>
                    {t.noTarget}
                  </td>
                  <td className='px-4 py-2 text-right tabular-nums text-muted-foreground'>
                    {t.onTime + t.late}
                  </td>
                  <td className='px-4 py-2 text-right font-medium tabular-nums'>
                    {t.onTimePct === null ? '—' : `${t.onTimePct}%`}
                  </td>
                </tr>
              ))}
            </tbody>
            {report.byType.length > 0 && (
              <tfoot>
                <tr className='border-t bg-muted/30 font-medium'>
                  <td className='px-4 py-2'>Total</td>
                  <td className='px-4 py-2 text-right tabular-nums'>{report.onTime}</td>
                  <td className='px-4 py-2 text-right tabular-nums'>{report.late}</td>
                  <td className='px-4 py-2 text-right tabular-nums'>{report.noTarget}</td>
                  <td className='px-4 py-2 text-right tabular-nums'>{judged}</td>
                  <td className='px-4 py-2 text-right tabular-nums'>
                    {report.onTimePct === null ? '—' : `${report.onTimePct}%`}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </ReportSection>

      {/* ── Every judged completion ───────────────────────────────────────── */}
      <ReportSection
        title='Details'

        onExport={report.rows.length > 0 ? exportCsv : undefined}
        footer={
          <ReportPager
            page={current}
            pageCount={pageCount}
            from={report.rows.length === 0 ? 0 : start + 1}
            to={Math.min(start + PAGE_SIZE, report.rows.length)}
            total={report.rows.length}
            onPage={setPage}
          />
        }
      >
        <div className='overflow-x-persistent'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground'>
                <th className='px-4 py-2 font-medium'>Completed</th>
                <th className='px-4 py-2 font-medium'>Type</th>
                <th className='px-4 py-2 font-medium'>Template</th>
                <th className='px-4 py-2 font-medium'>Work order</th>
                <th className='px-4 py-2 font-medium'>Patient</th>
                <th className='px-4 py-2 font-medium'>Due</th>
                <th className='px-4 py-2 font-medium'>Verdict</th>
                <th className='px-4 py-2 text-right font-medium'>Variance</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className='px-4 py-6 text-center text-xs text-muted-foreground'>
                    Nothing completed in this window.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className='border-b last:border-0'>
                  <td className='whitespace-nowrap px-4 py-2 tabular-nums'>
                    {new Date(r.completedAt).toLocaleDateString()}
                  </td>
                  <td className='whitespace-nowrap px-4 py-2'>
                    {r.type ? (PROCEDURE_TYPE_LABELS[r.type] ?? r.type) : '—'}
                  </td>
                  <td className='max-w-[16rem] truncate px-4 py-2' title={r.templateName}>
                    {r.templateName}
                  </td>
                  <td className='whitespace-nowrap px-4 py-2 font-mono text-xs'>
                    {r.workOrderNumber ?? '—'}
                  </td>
                  <td className='whitespace-nowrap px-4 py-2'>{r.patientMrn ?? '—'}</td>
                  <td className='whitespace-nowrap px-4 py-2 tabular-nums'>
                    {r.dueDate ? new Date(r.dueDate).toLocaleDateString() : '—'}
                  </td>
                  <td className='px-4 py-2'>
                    {/* Labelled, never colour alone. */}
                    <span
                      className='whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium'
                      style={{
                        background: `${VERDICT[r.verdict].color}1a`,
                        color: VERDICT[r.verdict].color,
                      }}
                    >
                      {VERDICT[r.verdict].label}
                    </span>
                  </td>
                  <td className='whitespace-nowrap px-4 py-2 text-right tabular-nums'>
                    {r.varianceDays === null ? (
                      <span className='text-muted-foreground'>—</span>
                    ) : (
                      <span
                        className={r.varianceDays > 0 ? 'text-orange-600' : 'text-green-600'}
                      >
                        {r.varianceDays > 0 ? `${r.varianceDays}d late` : `${Math.abs(r.varianceDays)}d early`}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ReportSection>
    </div>
  )
}

function Stat({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div>
      <dt className='flex items-center gap-1.5 text-xs text-muted-foreground'>
        {color && <span aria-hidden className='h-2 w-2 rounded-sm' style={{ background: color }} />}
        {label}
      </dt>
      <dd className='text-xl font-semibold tabular-nums text-foreground'>{value}</dd>
    </div>
  )
}
